import { useEffect, useRef, useState } from "react";
import { Send, Bot, User, Loader2, Mic, MicOff, Volume2, VolumeX, Square, Play, AudioLines, Paperclip, File as FileIcon, X } from "lucide-react";
import { api, apiErrorMessage, API_BASE } from "@/lib/api";

/**
 * /agent — chat with Jeff, the CPR operations agent (default landing page).
 * Mobile-first: full-height column, sticky composer, bubbles.
 * Talks to the Hermes bridge: POST /api/bridge/chat.
 * Voice: mic button (Web Speech API) for dictation; replies playable as audio
 * (edge-tts via POST /api/bridge/tts) with auto-play toggle.
 */

const SpeechRec =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

// Voice bubbles + attachments load via media elements, not the axios instance,
// so auth rides only on cookies. Auth cookies are scoped to the API host
// (api.paintcpr.com); media URLs must be ABSOLUTE via the baked-in base or the
// request goes to admin.paintcpr.com cookieless and 401s.
const mediaUrl = (u) => {
    if (!u || !u.startsWith("/") || !API_BASE || !API_BASE.startsWith("http"))
        return u;
    // API_BASE already ends in /api; backend media urls start with /api too.
    const rest = u.replace(/^\/api/, "");
    return API_BASE + rest;
};

export default function AgentChat() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [listening, setListening] = useState(false);
    const [voiceOn, setVoiceOn] = useState(false);
    const [speakingId, setSpeakingId] = useState(null);
    const endRef = useRef(null);
    const recRef = useRef(null);
    const audioRef = useRef(null);
    const mediaRef = useRef(null);
    const recStreamRef = useRef(null);
    const [recording, setRecording] = useState(false);
    const [recSeconds, setRecSeconds] = useState(0);
    const recTimerRef = useRef(null);
    const [uploadingVoice, setUploadingVoice] = useState(false);
    const [pendingFile, setPendingFile] = useState(null); // {file, dataUrl}
    const fileInputRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, busy]);

    const speak = async (text, idx) => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setSpeakingId(idx);
        try {
            const { data } = await api.post(
                "/bridge/tts",
                { text },
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(data);
            const a = new Audio(url);
            audioRef.current = a;
            a.onended = () => setSpeakingId(null);
            a.onerror = () => setSpeakingId(null);
            await a.play();
        } catch (e) {
            setSpeakingId(null);
            setErr("Voice playback unavailable — check your connection.");
        }
    };

    const toggleAutoVoice = () => {
        const next = !voiceOn;
        setVoiceOn(next);
        try {
            localStorage.setItem("agent_voice_on", next ? "1" : "0");
        } catch (e) {}
        if (!next && audioRef.current) {
            audioRef.current.pause();
            setSpeakingId(null);
        }
    };

    useEffect(() => {
        try {
            if (localStorage.getItem("agent_voice_on") === "1") setVoiceOn(true);
        } catch (e) {}
        return () => {
            if (audioRef.current) audioRef.current.pause();
        };
    }, []);

    const toggleMic = () => {
        if (!SpeechRec) {
            setErr("Voice input isn't supported in this browser. Try Chrome or Safari on your phone.");
            return;
        }
        if (listening) {
            recRef.current?.stop();
            setListening(false);
            return;
        }
        const rec = new SpeechRec();
        rec.lang = "en-US";
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.continuous = false;
        rec.onresult = (ev) => {
            const text = ev.results?.[0]?.[0]?.transcript || "";
            if (text) {
                setInput(text);
                setTimeout(() => send(text), 100);
            }
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => {
            setListening(false);
            setErr("Couldn't hear that — tap the mic and try again.");
        };
        recRef.current = rec;
        try {
            rec.start();
            setListening(true);
        } catch (e) {
            setListening(false);
        }
    };


    // ---- Discord-style voice: record actual mic audio, upload it, transcribe, send ----
    const stopTrackStream = () => {
        if (recStreamRef.current) {
            recStreamRef.current.getTracks().forEach((t) => t.stop());
            recStreamRef.current = null;
        }
    };

    const cancelRecording = () => {
        mediaRef.current?.stop();
        stopTrackStream();
        clearInterval(recTimerRef.current);
        setRecording(false);
        setRecSeconds(0);
    };

    const toggleRecord = async () => {
        if (recording) {
            cancelRecording();
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setErr("Voice recording isn't supported in this browser. Try Chrome on your phone, or use the mic for dictation.");
            return;
        }
        try {
            setErr("");
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            recStreamRef.current = stream;
            // Prefer opus/webm when available (what MediaRecorder gives on Chrome).
            const mime =
                MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                    ? "audio/webm;codecs=opus"
                    : MediaRecorder.isTypeSupported("audio/webm")
                    ? "audio/webm"
                    : "";
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            const chunks = [];
            rec.ondataavailable = (e) => {
                if (e.data && e.data.size) chunks.push(e.data);
            };
            rec.onstop = async () => {
                stopTrackStream();
                clearInterval(recTimerRef.current);
                setRecording(false);
                const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
                await sendVoiceBlob(blob);
            };
            mediaRef.current = rec;
            rec.start(500);
            setRecording(true);
            setRecSeconds(0);
            recTimerRef.current = setInterval(() => setRecSeconds((s) => s + 1), 1000);
        } catch (e) {
            stopTrackStream();
            setRecording(false);
            setErr("Couldn't access the microphone — allow the mic prompt and try again.");
        }
    };

    const sendVoiceBlob = async (blob) => {
        if (!blob || !blob.size) return;
        setUploadingVoice(true);
        try {
            // Base64-encode the blob for the /voice endpoint (JSON body).
            const b64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            const { data } = await api.post("/bridge/voice", {
                session_id: sessionId,
                data_url: b64,
                mime: blob.type || "audio/webm",
            });
            // Render the user's voice bubble in the thread.
            setMessages((m) => [
                ...m,
                { role: "user", voice: data, content: "" },
            ]);
            // Transcribe the recording, then send the transcript as the message.
            // Pass the voice object so /chat links transcript + reply to the bubble.
            const text = await transcribeBlob(blob);
            if (text) send(text, data);
            else
                setMessages((m) => [
                    ...m,
                    {
                        role: "assistant",
                        content: "I couldn't catch a word in that recording — tap the mic and re-record.",
                    },
                ]);
        } catch (e) {
            setErr("Voice message didn't upload — check your connection and try again.");
        } finally {
            setUploadingVoice(false);
        }
    };

    // Encode a blob as 16-bit PCM WAV via OfflineAudioContext (Groq whisper wants wav).
    const toWav = async (blob) => {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return blob;
        try {
            const ac = new AC();
            const buf = await ac.decodeAudioData(await blob.arrayBuffer());
            const off = new OfflineAudioContext(1, buf.length, buf.sampleRate);
            const src = off.createBufferSource();
            src.buffer = buf; src.connect(off.destination); src.start(0);
            const rendered = await off.startRendering();
            const ch = rendered.getChannelData(0);
            const out = new DataView(new ArrayBuffer(44 + ch.length * 2));
            const ws = (o, str) => { for (let i = 0; i < str.length; i++) out.setUint8(o + i, str.charCodeAt(i)); };
            ws(0, "RIFF"); out.setUint32(4, 36 + ch.length * 2, true); ws(8, "WAVE");
            ws(12, "fmt "); out.setUint32(16, 16, true); out.setUint16(20, 1, true);
            out.setUint16(22, 1, true); out.setUint32(24, rendered.sampleRate, true);
            out.setUint32(28, rendered.sampleRate * 2, true); out.setUint16(32, 2, true);
            out.setUint16(34, 16, true); ws(36, "data"); out.setUint32(40, ch.length * 2, true);
            for (let i = 0; i < ch.length; i++) {
                const v = Math.max(-1, Math.min(1, ch[i]));
                out.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
            }
            return new Blob([out], { type: "audio/wav" });
        } catch (e) {
            return blob; // fall back to the original (webm) if decode fails
        }
    };

    // Multipart upload to /stt (Groq whisper) for an already-recorded blob.
    const transcribeBlob = async (blob, timeoutMs = 90000) => {
        try {
            const wav = await toWav(blob);
            const form = new FormData();
            form.append("audio", wav, wav.type === "audio/wav" ? "recording.wav" : "recording.webm");
            const ctrl = new AbortController();
            const to = setTimeout(() => ctrl.abort(), timeoutMs);
            const { data } = await api.post("/bridge/stt", form, {
                headers: { "Content-Type": "multipart/form-data" },
                signal: ctrl.signal,
            });
            clearTimeout(to);
            return (data?.text || "").trim();
        } catch (e) {
            return "";
        }
    };

    const refreshSessions = async () => {
        try {
            const { data } = await api.get("/bridge/sessions");
            setSessions(data.sessions || []);
        } catch (e) {}
    };
    // One-thread default: on load, resume the most recent conversation instead of
    // opening a blank thread. (No manual "new chat" — the backend auto-resets the
    // thread only after 7d inactivity or when it outgrows the context window.)
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/bridge/sessions");
                const list = data.sessions || [];
                setSessions(list);
                if (list.length > 0) await loadSession(list[0].session_id);
            } catch (e) {}
        })();
    }, []);

    const loadSession = async (sid) => {
        try {
            const { data } = await api.get(`/bridge/sessions/${sid}`);
            setSessionId(sid);
            setMessages((data.history || []).map((h) => {
                const m = { role: h.role, content: h.content };
                if (h.voice) m.voice = h.voice;
                if (h.attachment) m.attachment = h.attachment;
                return m;
            }));
            setShowHistory(false);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    const newSession = () => {
        setSessionId(null);
        setMessages([]);
        setShowHistory(false);
    };

    const renameSession = async (sid, current) => {
        const t = window.prompt("Rename this conversation:", current || "");
        if (t === null) return;
        try {
            await api.patch(`/bridge/sessions/${sid}`, { title: t });
            refreshSessions();
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    const deleteSession = async (sid) => {
        if (!window.confirm("Delete this conversation?")) return;
        try {
            await api.delete(`/bridge/sessions/${sid}`);
            if (sid === sessionId) newSession();
            refreshSessions();
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    const readFileAsDataUrl = (file) =>
        new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(file);
        });

    const pickFile = () => fileInputRef.current?.click();

    const onFileChosen = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 20 * 1024 * 1024) {
            setErr("Files are limited to 20 MB.");
            return;
        }
        try {
            const dataUrl = await readFileAsDataUrl(f);
            setPendingFile({ file: f, dataUrl });
        } catch (err) {
            setErr("Could not read that file.");
        }
    };

    const removePendingFile = () => setPendingFile(null);

    const send = async (text, voiceObj = null) => {
        const msg = (text ?? input).trim();
        if ((!msg && !voiceObj) || busy) return;
        setErr("");
        setInput("");
        const pendingSnapshot = pendingFile
            ? { name: pendingFile.file.name, url: URL.createObjectURL(pendingFile.file),
                mime: pendingFile.file.type || "application/octet-stream", local: true }
            : null;
        const userMsg = { role: "user", content: msg, attachment: pendingSnapshot };
        setMessages((m) => [...m, userMsg]);
        setBusy(true);
        try {
            let attachment = null;
            if (pendingFile) {
                attachment = {
                    name: pendingFile.file.name,
                    mime: pendingFile.file.type || "application/octet-stream",
                    data_url: pendingFile.dataUrl,
                };
            }
            const { data } = await api.post("/bridge/chat", {
                message: msg,
                session_id: sessionId,
                attachment,
                voice_id: voiceObj ? voiceObj.id : undefined,
            });
            setSessionId(data.session_id);
            setPendingFile(null);
            if (data.attachment) {
                const a = data.attachment;
                setMessages((m) => {
                    const copy = [...m];
                    for (let k = copy.length - 1; k >= 0; k--) {
                        if (copy[k].role === "user") {
                            copy[k] = { ...copy[k], attachment: { name: a.name, url: a.url, mime: a.mime, local: false, bytes: a.bytes } };
                            break;
                        }
                    }
                    return copy;
                });
            }
            setMessages((m) => {
                const next = [...m, { role: "assistant", content: data.reply }];
                if (voiceOn) {
                    const idx = next.length - 1;
                    setTimeout(() => speak(data.reply, idx), 150);
                }
                return next;
            });
        } catch (e) {
            const d = e.response?.data?.detail;
            const code = typeof d === "object" && d?.code ? d.code : "";
            const friendly =
                code === "hermes_timeout"
                    ? "The agent took too long to reply. Try again."
                    : code === "llm_unavailable"
                    ? "The agent's LLM is unavailable right now. Try again shortly."
                    : apiErrorMessage(e);
            setErr(friendly);
            setMessages((m) => [
                ...m,
                { role: "assistant", content: "⚠️ " + friendly },
            ]);
        } finally {
            setBusy(false);
            refreshSessions();
        }
    };

    const suggestions = [
        "Summarize my new leads",
        "Draft a follow-up for the newest lead",
        "What jobs are scheduled this week?",
    ];

    return (
        <div className="page-shell agent-page">
            <header className="fade-up">
                <p className="eyebrow">Jeff — your operations agent</p>
                <h1 className="h1 mt-3">Chat with Jeff</h1>
                <p className="text-muted mt-2 max-w-[680px]">
                    Ask about leads, estimates, jobs, or the schedule. Jeff can draft
                    estimates and messages — every price and every outbound message
                    needs your approval before it goes anywhere.
                </p>
            </header>

            <div className="agent-thread brand-card" data-testid="agent-thread">
                {messages.length === 0 && (
                    <div className="agent-empty">
                        <Bot size={28} />
                        <p>Say hello — by text or with the mic — or try:</p>
                        <div className="agent-suggestions">
                            {suggestions.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    className="agent-suggestion"
                                    onClick={() => send(s)}
                                    disabled={busy}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <div key={i} className={"agent-msg agent-msg-" + m.role}>
                        <div className="agent-msg-avatar">
                            {m.role === "user" ? <User size={16} /> : <Bot size={16} />}
                        </div>
                        {m.voice && (
                            <audio
                                className="agent-voice-audio"
                                controls
                                preload="metadata"
                                src={mediaUrl(m.voice.url)}
                                data-testid={"agent-voice-audio-" + i}
                            />
                        )}
                        {m.attachment && (
                            <div className={"agent-attach-chip" + (m.attachment.local ? " pending" : "")}>
                                {String(m.attachment.mime || "").startsWith("image/") ? (
                                    <img className="agent-attach-thumb" src={mediaUrl(m.attachment.url)} alt={m.attachment.name} />
                                ) : (
                                    <FileIcon size={16} className="agent-attach-icon" />
                                )}
                                <a
                                    className="agent-attach-name"
                                    href={mediaUrl(m.attachment.url)}
                                    download={m.attachment.name}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {m.attachment.name}
                                </a>
                            </div>
                        )}
                        {m.content && <div className="agent-msg-bubble">{m.content}</div>}
                        {m.voice && !m.content && (
                            <div className="agent-msg-bubble agent-transcribing">
                                Transcribing your voice note…
                            </div>
                        )}
                        {m.role === "assistant" && !m.content.startsWith("⚠️") && (
                            <button
                                type="button"
                                className="agent-voice-btn"
                                title="Play Jeff's reply"
                                aria-label="Play reply as audio"
                                onClick={() =>
                                    speakingId === i
                                        ? (audioRef.current?.pause(), setSpeakingId(null))
                                        : speak(m.content, i)
                                }
                                data-testid={"agent-voice-" + i}
                            >
                                {speakingId === i ? <VolumeX size={14} /> : <Volume2 size={14} />}
                            </button>
                        )}
                    </div>
                ))}
                {busy && (
                    <div className="agent-msg agent-msg-assistant">
                        <div className="agent-msg-avatar">
                            <Bot size={16} />
                        </div>
                        <div className="agent-msg-bubble agent-typing">
                            <Loader2 className="spin" size={16} /> Jeff is thinking…
                        </div>
                    </div>
                )}
                <div ref={endRef} />
            </div>

            <div className="agent-sessionbar">
                <button
                    type="button"
                    className="agent-history-toggle"
                    onClick={() => setShowHistory((s) => !s)}
                    data-testid="agent-history-toggle"
                >
                    Conversations {showHistory ? "▲" : "▼"}
                </button>
            </div>
            {showHistory && (
                <div className="agent-history brand-card" data-testid="agent-history">
                    {sessions.length === 0 && (
                        <p className="agent-history-empty">No past conversations yet.</p>
                    )}
                    {sessions.map((s) => (
                        <div key={s.session_id} className="agent-history-item">
                            <button
                                type="button"
                                className="agent-history-open"
                                onClick={() => loadSession(s.session_id)}
                                title={s.title}
                            >
                                <span className="agent-history-title">{s.title}</span>
                                <span className="agent-history-meta">
                                    {s.message_count} msg
                                    {s.last_ts
                                        ? " · " + new Date(s.last_ts * 1000).toLocaleDateString()
                                        : ""}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="agent-history-btn"
                                title="Rename"
                                aria-label="Rename conversation"
                                onClick={() => renameSession(s.session_id, s.title)}
                            >
                                ✎
                            </button>
                            <button
                                type="button"
                                className="agent-history-btn"
                                title="Delete"
                                aria-label="Delete conversation"
                                onClick={() => deleteSession(s.session_id)}
                            >
                                🗑
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <form
                className="agent-composer"
                onSubmit={(e) => {
                    e.preventDefault();
                    send();
                }}
            >
                {pendingFile && (
                    <div className="agent-pending-file" data-testid="agent-pending-file">
                        <FileIcon size={15} />
                        <span className="agent-pending-name">{pendingFile.file.name}</span>
                        <button
                            type="button"
                            className="agent-pending-remove"
                            onClick={removePendingFile}
                            aria-label="Remove attachment"
                            data-testid="agent-attach-remove"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={onFileChosen}
                    data-testid="agent-file-input"
                />
                <button
                    type="button"
                    className="agent-attach-btn"
                    onClick={pickFile}
                    title="Attach a file (image, PDF, or document)"
                    aria-label="Attach file"
                    disabled={busy || recording}
                    data-testid="agent-attach"
                >
                    <Paperclip size={18} />
                </button>
                <button
                    type="button"
                    className={
                        "agent-mic agent-record" + (recording ? " recording" : "")
                    }
                    onClick={toggleRecord}
                    title="Record a voice note (tap, hold the line, tap again to send)"
                    aria-label="Record voice note"
                    disabled={busy || uploadingVoice || listening}
                    data-testid="agent-record"
                >
                    {recording ? (
                        <span className="agent-record-inner">
                            <Square size={16} />
                            {Math.floor(recSeconds / 60)}:
                            {(recSeconds % 60).toString().padStart(2, "0")}
                        </span>
                    ) : (
                        <span className="agent-mic-wrap">
                            <AudioLines size={18} />
                            <span className="agent-mic-label">Record a voice note</span>
                        </span>
                    )}
                </button>
                {recording && (
                    <span className="agent-rec-hint">
                        Voice note — tap ⏹ to send
                    </span>
                )}
                <button
                    type="button"
                    className={"agent-mic" + (listening ? " listening" : "")}
                    onClick={toggleMic}
                    title="Hold a conversation with your voice"
                    aria-label="Voice input"
                    disabled={busy}
                    data-testid="agent-mic"
                >
                    <span className="agent-mic-wrap">
                        {listening ? <MicOff size={18} /> : <Mic size={18} />}
                        <span className="agent-mic-label">
                            {listening ? "Stop listening" : "Type by talking"}
                        </span>
                    </span>
                </button>
                <textarea
                    className="agent-input agent-textarea"
                    rows={2}
                    placeholder={recording ? "Recording voice note…" : listening ? "Listening…" : "Message Jeff…"}
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        const el = e.target;
                        el.style.height = "auto";
                        el.style.height = Math.min(el.scrollHeight, 200) + "px";
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    disabled={busy || recording}
                    data-testid="agent-input"
                />
                <button
                    className="agent-send"
                    type="submit"
                    disabled={busy || uploadingVoice || !input.trim()}
                    data-testid="agent-send"
                >
                    {busy || uploadingVoice ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
                </button>
            </form>
            <div className="agent-voice-row">
                <button
                    type="button"
                    className={"agent-voice-toggle" + (voiceOn ? " on" : "")}
                    onClick={toggleAutoVoice}
                    data-testid="agent-voice-toggle"
                >
                    <Volume2 size={14} />
                    {voiceOn ? "Jeff talks back — ON" : "Jeff talks back — OFF"}
                </button>
                {err && <p className="agent-error">{err}</p>}
            </div>
        </div>
    );
}
